import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, Shield, X, BadgeInfo, Volume2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { isAdmin } from "../../services/authService";
import ChatHeader from "./components/ChatHeader";
import ChatInput from "./components/ChatInput";
import ChatMessage from "./components/ChatMessage";
import TypingIndicator from "./components/TypingIndicator";
import wealthChatbotService from "./api";
import { buildApiUrl } from "../../services/apiConfig";
import io from "socket.io-client";
import VoiceSphere from "./components/VoiceSphere";
import { useLlmConfig } from "../../contexts/LlmConfigContext";

const WealthChatBot = ({
    cust_id,
    use_case_id,
    tab_id = "default",
    advisor_id = null,
    onWidgetCreated = null
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [messages, setMessages] = useState([
        {
            text: "Hello! I'm your wealth management assistant. How can I help you today?",
            sender: "bot"
        }
    ]);
    const [input, setInput] = useState("");
    const [files, setFiles] = useState([]);
    const [queryPopupIndex, setQueryPopupIndex] = useState(null);
    const [userIsAdmin, setUserIsAdmin] = useState(false);
    const [ratedMessages, setRatedMessages] = useState({});
    const [userId, setUserId] = useState(advisor_id);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(null);
    const [isCreatingWidget, setIsCreatingWidget] = useState(false);
    const [runId, setRunId] = useState(null);
    const [activeTab, setActiveTab] = useState("audit");
    const [useNovaV2, setUseNovaV2] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const { llmConfig } = useLlmConfig();

    const [isVoiceActive, setIsVoiceActive] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [voiceTranscription, setVoiceTranscription] = useState("");


    const runIdRef = useRef(null);
    const pendingApprovalRunIdRef = useRef(null); // run_id of an interrupt awaiting a reply
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const popupRef = useRef(null);
    const socketRef = useRef(null);
    const audioContextRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const processorRef = useRef(null);
    const transcriptionTextRef = useRef("");
    const confirmedTranscriptRef = useRef(""); // accumulates finalized ## sentences
    const silenceTimerRef = useRef(null);
    const audioRef = useRef(null);
    const isVoiceBusyRef = useRef(false);   // prevents concurrent Nova/TTS requests
    const isVoiceActiveRef = useRef(false); // ref so async callbacks see current value

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!socketRef.current) {
            const socketUrl = buildApiUrl('SOCKET');

            socketRef.current = io(socketUrl, {
                path: '/socket.io/',
                transports: ["websocket", "polling"],
                reconnectionAttempts: 5
            });

            const socket = socketRef.current;

            socket.on("transcription", (data) => {
                if (!data || !data.text) return;

                const rawText = data.text;

                if (rawText.startsWith("##")) {
                    // Finalized sentence — append to confirmed buffer
                    const finalizedSentence = rawText.replace(/^##\s*/, '').trim();
                    const prefix = confirmedTranscriptRef.current ? confirmedTranscriptRef.current + " " : "";
                    confirmedTranscriptRef.current = prefix + finalizedSentence;
                    transcriptionTextRef.current = confirmedTranscriptRef.current;
                    setVoiceTranscription(confirmedTranscriptRef.current);
                } else if (rawText.startsWith(">>")) {
                    // Interim rolling window — show confirmed + current interim preview
                    const interim = rawText.replace(/^>>\s*/, '').trim();
                    const combined = confirmedTranscriptRef.current
                        ? confirmedTranscriptRef.current + " " + interim
                        : interim;
                    transcriptionTextRef.current = combined;
                    setVoiceTranscription(combined);
                } else {
                    // Plain text fallback
                    const prefix = confirmedTranscriptRef.current ? confirmedTranscriptRef.current + " " : "";
                    const combined = prefix + rawText.trim();
                    transcriptionTextRef.current = combined;
                    setVoiceTranscription(combined);
                }
            });

            socket.on("connect_error", (error) => {
                console.error("Socket connection error:", error);
            });
        }

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        setUserIsAdmin(isAdmin());
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isExpanded]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setQueryPopupIndex(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Initialize userId from props or session storage
    useEffect(() => {
        if (advisor_id) {
            setUserId(advisor_id);
        } else {
            const storedUserId = sessionStorage.getItem('user_id');
            setUserId(storedUserId || 'U1757535255');
        }
    }, [advisor_id]);

    useEffect(() => {
        if (useNovaV2) {
            wealthChatbotService.getSuggestions().then(setSuggestions);
        } else {
            setSuggestions([]);
        }
    }, [useNovaV2]);



    const handleStartRecording = async () => {
        if (isListening) return;

        setVoiceTranscription("");
        transcriptionTextRef.current = "";
        confirmedTranscriptRef.current = "";

        try {
            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;

            // Set up audio context
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioContext();
            audioContextRef.current = audioContext;

            // Load audio worklet
            try {
                await audioContext.audioWorklet.addModule('/audio-processor.js');
            } catch (error) {
                console.error("Failed to load audio worklet:", error);
                throw new Error("Failed to initialize audio processing");
            }

            // Set up audio processing
            const source = audioContext.createMediaStreamSource(stream);
            const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');

            workletNode.port.onmessage = (event) => {
                try {
                    if (event.data.audioData) {
                        const inputData = event.data.audioData;
                        const originalSampleRate = audioContext.sampleRate;
                        const targetSampleRate = 16000;

                        // Downsample the audio
                        const downsampled = downsampleBuffer(inputData, originalSampleRate, targetSampleRate);
                        const pcmBuffer = floatTo16BitPCM(downsampled);
                        const binaryData = new Uint8Array(pcmBuffer);

                        // Send to socket for transcription
                        if (socketRef.current && socketRef.current.connected) {
                            socketRef.current.emit("audio_stream", binaryData);
                        }
                    }
                } catch (err) {
                    console.error("Error processing audio:", err);
                }
            };

            source.connect(workletNode);
            workletNode.connect(audioContext.destination);
            processorRef.current = workletNode;

            return true;
        } catch (error) {
            console.error("Error starting recording:", error);
            return false;
        }
    };

    const handleStopRecording = async () => {
        try {
            // Stop sending audio to the server
            if (socketRef.current) {
                socketRef.current.emit("stop_audio");
            }

            // Clean up audio processing resources
            if (processorRef.current) {
                processorRef.current.port.postMessage({ terminate: true });
                processorRef.current.disconnect();
                processorRef.current = null;
            }

            // Stop all audio tracks
            if (mediaStreamRef.current) {
                const tracks = mediaStreamRef.current.getTracks();
                tracks.forEach(track => track.stop());
                mediaStreamRef.current = null;
            }

            // Close audio context
            if (audioContextRef.current) {
                await audioContextRef.current.close();
                audioContextRef.current = null;
            }

            return transcriptionTextRef.current;
        } catch (error) {
            console.error("Error stopping recording:", error);
            return transcriptionTextRef.current;
        }
    };

    // Add these utility functions for audio processing
    const downsampleBuffer = (buffer, originalSampleRate, targetSampleRate) => {
        if (originalSampleRate === targetSampleRate) {
            return buffer;
        }

        const ratio = originalSampleRate / targetSampleRate;
        const newLength = Math.round(buffer.length / ratio);
        const result = new Float32Array(newLength);

        for (let i = 0; i < newLength; i++) {
            const pos = Math.floor(i * ratio);
            result[i] = buffer[pos];
        }

        return result;
    };

    const floatTo16BitPCM = (input) => {
        const output = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        return output.buffer;
    };

    const startVoiceInteraction = async () => {
        // Prevent starting while a Nova/TTS cycle is in progress
        if (isVoiceBusyRef.current) return;

        // Clear any leftover silence timer from a previous session
        if (silenceTimerRef.current) {
            clearInterval(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }

        isVoiceActiveRef.current = true;
        transcriptionTextRef.current = "";
        setIsVoiceActive(true);
        setVoiceTranscription("");

        const success = await handleStartRecording();

        if (success) {
            setIsListening(true);

            let lastTranscription = transcriptionTextRef.current;
            let silenceCounter = 0;

            const checkSilence = () => {
                // Stop checking if voice mode was closed externally
                if (!silenceTimerRef.current) return;

                if (transcriptionTextRef.current === lastTranscription) {
                    silenceCounter++;

                    // 3 seconds of silence (6 checks at 500ms intervals)
                    if (silenceCounter >= 6 && transcriptionTextRef.current.trim().length > 0) {
                        clearInterval(silenceTimerRef.current);
                        silenceTimerRef.current = null;
                        stopVoiceInteraction();
                    }
                } else {
                    silenceCounter = 0;
                    lastTranscription = transcriptionTextRef.current;
                }
            };

            silenceTimerRef.current = setInterval(checkSilence, 500);
        } else {
            isVoiceActiveRef.current = false;
            setIsVoiceActive(false);
            alert("Could not access microphone. Please check your permissions and try again.");
        }
    };
    const stopVoiceInteraction = async () => {
        if (isVoiceBusyRef.current) return;
        isVoiceBusyRef.current = true;
        if (silenceTimerRef.current) {
            clearInterval(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        setIsListening(false);
        setIsProcessing(true);
        await handleStopRecording();

        // Get the final transcription
        const finalTranscription = transcriptionTextRef.current.trim();
        setVoiceTranscription(finalTranscription);

        if (finalTranscription) {
            // Process the transcription with NOVA API
            try {


                // Send message to NOVA API
                const userMessage = { text: finalTranscription, sender: "user" };
                setMessages(prevMessages => [...prevMessages, userMessage]);

                const response = useNovaV2
                    ? await wealthChatbotService.sendMessageV2(finalTranscription, cust_id, userId, use_case_id, llmConfig)
                    : await wealthChatbotService.sendMessage(finalTranscription, [], cust_id, userId, getPageName(), use_case_id);

                let accumulatedText = "";
                let sqlQuery = null;
                let ttsTriggered = false;

                const triggerTTS = async () => {
                    if (ttsTriggered || !accumulatedText.trim()) return;
                    ttsTriggered = true;

                    const cleanText = accumulatedText
                        .replace(/\*\*(.*?)\*\*/g, "$1")
                        .replace(/\*(.*?)\*/g, "$1")
                        .replace(/<[^>]*>/g, "")
                        .replace(/#{1,6}\s+/g, "")
                        .replace(/\|.*?\|/g, "") // strip markdown table cells for TTS
                        .replace(/[-:]+/g, "");

                    setIsProcessing(false);
                    setIsSpeaking(true);

                    const ttsResult = await wealthChatbotService.generateTextToSpeech(cleanText);

                    const restartListening = () => {
                        isVoiceBusyRef.current = false; // release the busy guard before restarting
                        transcriptionTextRef.current = "";
                        confirmedTranscriptRef.current = "";
                        setVoiceTranscription("");
                        // Only restart if the user hasn't closed the voice interface
                        if (isVoiceActiveRef.current) {
                            startVoiceInteraction();
                        }
                    };

                    if (ttsResult.success) {
                        const audioUrl = URL.createObjectURL(ttsResult.audioBlob);
                        const audio = new Audio(audioUrl);
                        audioRef.current = audio; // store so closeVoiceInterface can stop it
                        audio.onended = () => {
                            audioRef.current = null;
                            setIsSpeaking(false);
                            URL.revokeObjectURL(audioUrl);
                            restartListening();
                        };
                        audio.play();
                    } else {
                        console.error("TTS failed:", ttsResult.error);
                        setIsSpeaking(false);
                        restartListening();
                    }
                };

                try {
                    const { createParser } = await import('eventsource-parser');

                    const parser = createParser({
                        onEvent: async (event) => {
                            const type = event.event;
                            if (type === "filler" || type === "interrupt" || type === "final") {
                                try {
                                    const parsedData = JSON.parse(event.data);

                                    if (parsedData.ERROR && typeof parsedData.ERROR === 'string') {
                                        throw new Error(parsedData.ERROR);
                                    }

                                    // filler events send data as a plain string, not an object
                                    if (type === "filler") {
                                        const fillerText = typeof parsedData.data === "string"
                                            ? parsedData.data
                                            : parsedData.data?.response || "";
                                        if (fillerText) {
                                            accumulatedText += fillerText;
                                            setMessages(prevMessages => {
                                                const updatedMessages = [...prevMessages];
                                                const lastIndex = updatedMessages.length - 1;
                                                if (lastIndex >= 0 && updatedMessages[lastIndex].sender === "bot") {
                                                    updatedMessages[lastIndex] = { text: styleText(accumulatedText), sender: "bot", audit: null, isLoadingAudit: false };
                                                } else {
                                                    updatedMessages.push({ text: styleText(accumulatedText), sender: "bot", audit: null, isLoadingAudit: false });
                                                }
                                                return updatedMessages;
                                            });
                                        }
                                        return;
                                    }

                                    // interrupt / final events have data as an object
                                    if (parsedData.data?.query) {
                                        sqlQuery = parsedData.data.query;
                                    }

                                    if (parsedData.data?.response) {
                                        accumulatedText += parsedData.data.response;

                                        setMessages(prevMessages => {
                                            const updatedMessages = [...prevMessages];
                                            const lastIndex = updatedMessages.length - 1;

                                            if (lastIndex >= 0 && updatedMessages[lastIndex].sender === "bot") {
                                                updatedMessages[lastIndex] = {
                                                    text: styleText(accumulatedText),
                                                    sender: "bot",
                                                    ...(sqlQuery && { query: sqlQuery }),
                                                    ...(parsedData.data.block_reason && { block_reason: parsedData.data.block_reason }),
                                                    run_id: parsedData.data.run_id || runIdRef.current,
                                                    audit: null,
                                                    isLoadingAudit: false
                                                };
                                            } else {
                                                updatedMessages.push({
                                                    text: styleText(accumulatedText),
                                                    sender: "bot",
                                                    ...(sqlQuery && { query: sqlQuery }),
                                                    ...(parsedData.data.block_reason && { block_reason: parsedData.data.block_reason }),
                                                    run_id: parsedData.data.run_id || runIdRef.current,
                                                    audit: null,
                                                    isLoadingAudit: false
                                                });
                                            }

                                            return updatedMessages;
                                        });

                                        if (type === "final") {
                                            await triggerTTS();
                                        }
                                    }
                                } catch (error) {
                                    console.error("Error processing event data:", error);
                                }
                            }
                        }
                    });

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();

                    while (true) {
                        const { value, done } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, { stream: true });
                        parser.feed(chunk);
                    }

                    // Stream ended without a "final" event (e.g. filler+interrupt flow) — trigger TTS now
                    if (!ttsTriggered) {
                        await triggerTTS();
                    }
                } catch (streamError) {
                    console.error("Stream processing error:", streamError);
                    setIsProcessing(false);
                    isVoiceBusyRef.current = false;
                }
            } catch (error) {
                console.error("Error processing voice input:", error);
                setMessages(prevMessages => [
                    ...prevMessages,
                    {
                        text: error.message || "Error connecting to server.",
                        sender: "bot"
                    }
                ]);
                setIsProcessing(false);
                isVoiceBusyRef.current = false;
            }
        } else {
            setIsProcessing(false);
            isVoiceBusyRef.current = false;
        }
    };


    const stopVoiceRecording = async () => {
        if (!isListening) return;

        setIsListening(false);
        setIsProcessing(true);

        try {
            if (socketRef.current) {
                socketRef.current.emit("stop_audio");
            }

            if (processorRef.current) {
                processorRef.current.port.postMessage({ terminate: true });
                processorRef.current.disconnect();
                processorRef.current = null;
            }

            if (mediaStreamRef.current) {
                const tracks = mediaStreamRef.current.getTracks();
                tracks.forEach(track => track.stop());
                mediaStreamRef.current = null;
            }

            if (audioContextRef.current) {
                await audioContextRef.current.close();
                audioContextRef.current = null;
            }

            // Process the final transcription
            const finalTranscription = transcriptionTextRef.current.trim();

            if (finalTranscription) {
                // Send the transcription to the NOVA API
                processVoiceTranscription(finalTranscription);
            } else {
                setIsProcessing(false);
                setVoiceTranscription("I didn't catch that. Please try again.");

                // Auto-close after a delay if no transcription
                setTimeout(() => {
                    if (isVoiceActive) {
                        closeVoiceInterface();
                    }
                }, 2000);
            }
        } catch (error) {
            console.error("Error stopping voice recording:", error);
            setIsProcessing(false);
        }
    };

    const processVoiceTranscription = async (transcription) => {
        try {
            // Add user message to chat
            const userMessage = { text: transcription, sender: "user" };
            setMessages(prevMessages => [...prevMessages, userMessage]);

            // Send to NOVA API
            const response = useNovaV2
                ? await wealthChatbotService.sendMessageV2(transcription, cust_id, userId, use_case_id, llmConfig)
                : await wealthChatbotService.sendMessage(transcription, [], cust_id, userId, getPageName(), use_case_id);

            let accumulatedText = "";
            let sqlQuery = null;

            try {
                const { createParser } = await import('eventsource-parser');

                const parser = createParser({
                    onEvent: (event) => {
                        const type = event.event;
                        if (type === "filler" || type === "interrupt" || type === "final") {
                            try {
                                const parsedData = JSON.parse(event.data);

                                if (parsedData.ERROR && typeof parsedData.ERROR === 'string') {
                                    throw new Error(parsedData.ERROR);
                                }

                                if (parsedData.data.query) {
                                    sqlQuery = parsedData.data.query;
                                }

                                if (parsedData.data.response) {
                                    accumulatedText += parsedData.data.response;

                                    setMessages(prevMessages => {
                                        const updatedMessages = [...prevMessages];
                                        const lastIndex = updatedMessages.length - 1;

                                        if (lastIndex >= 0 && updatedMessages[lastIndex].sender === "bot") {
                                            updatedMessages[lastIndex] = {
                                                text: styleText(accumulatedText),
                                                sender: "bot",
                                                ...(sqlQuery && { query: sqlQuery }),
                                                ...(parsedData.data.block_reason && { block_reason: parsedData.data.block_reason }),
                                                run_id: parsedData.data.run_id || runIdRef.current,
                                                audit: null,
                                                isLoadingAudit: false
                                            };
                                        } else {
                                            updatedMessages.push({
                                                text: styleText(accumulatedText),
                                                sender: "bot",
                                                ...(sqlQuery && { query: sqlQuery }),
                                                ...(parsedData.data.block_reason && { block_reason: parsedData.data.block_reason }),
                                                run_id: parsedData.data.run_id || runIdRef.current,
                                                audit: null,
                                                isLoadingAudit: false
                                            });
                                        }

                                        return updatedMessages;
                                    });

                                    if (type === "final") {
                                        // When we have the final response, convert it to speech
                                        convertResponseToSpeech(accumulatedText);
                                    }
                                } else if (parsedData.data) {
                                    accumulatedText += parsedData.data;

                                    setMessages(prevMessages => {
                                        const updatedMessages = [...prevMessages];
                                        const lastIndex = updatedMessages.length - 1;

                                        if (lastIndex >= 0 && updatedMessages[lastIndex].sender === "bot") {
                                            updatedMessages[lastIndex] = {
                                                text: styleText(accumulatedText),
                                                sender: "bot",
                                                ...(sqlQuery && { query: sqlQuery }),
                                                ...(parsedData.data.block_reason && { block_reason: parsedData.data.block_reason }),
                                                run_id: parsedData.data.run_id || runIdRef.current,
                                                audit: null,
                                                isLoadingAudit: false
                                            };
                                        } else {
                                            updatedMessages.push({
                                                text: styleText(accumulatedText),
                                                sender: "bot",
                                                ...(sqlQuery && { query: sqlQuery }),
                                                ...(parsedData.data.block_reason && { block_reason: parsedData.data.block_reason }),
                                                run_id: parsedData.data.run_id || runIdRef.current,
                                                audit: null,
                                                isLoadingAudit: false
                                            });
                                        }

                                        return updatedMessages;
                                    });

                                    if (type === "final") {
                                        // When we have the final response, convert it to speech
                                        convertResponseToSpeech(accumulatedText);
                                    }
                                }

                                if (parsedData.data.run_id) {
                                    runIdRef.current = parsedData.data.run_id;
                                }
                            } catch (error) {
                                console.error("Error processing event data:", error);
                            }
                        }
                    }
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();

                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    parser.feed(chunk);
                }
            } catch (streamError) {
                console.error("Stream processing error:", streamError);
                setIsProcessing(false);
                setVoiceTranscription("Sorry, there was an error processing your request.");
            }
        } catch (error) {
            console.error("Error processing voice transcription:", error);
            setMessages(prevMessages => [
                ...prevMessages,
                {
                    text: error.message || "Error connecting to server.",
                    sender: "bot"
                }
            ]);
            setIsProcessing(false);
            setVoiceTranscription("Sorry, there was an error processing your request.");
        }
    };

    const convertResponseToSpeech = async (text) => {
        setIsProcessing(false);

        try {
            // Clean the text for better TTS results
            const cleanText = text
                .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold markdown
                .replace(/\*(.*?)\*/g, "$1") // Remove italic markdown
                .replace(/<[^>]*>/g, "") // Remove HTML tags
                .replace(/#{1,6}\s+/g, "") // Remove heading markers
                .replace(/\|/g, ", ") // Replace table pipes with commas
                .replace(/\n/g, " ") // Replace newlines with spaces
                .replace(/\s+/g, " ") // Replace multiple spaces with a single space
                .trim();

            // Generate speech from the response
            setIsSpeaking(true);

            const ttsResult = await wealthChatbotService.generateTextToSpeech(cleanText);

            if (ttsResult.success) {
                // Play the audio
                const audioUrl = URL.createObjectURL(ttsResult.audioBlob);

                if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current = null;
                }

                const audio = new Audio(audioUrl);
                audioRef.current = audio;

                audio.onended = () => {
                    audioRef.current = null;
                    setIsSpeaking(false);
                    URL.revokeObjectURL(audioUrl);
                    isVoiceBusyRef.current = false;
                    // Auto-restart recording to continue the conversation
                    setTimeout(() => {
                        startVoiceInteraction();
                    }, 600);
                };

                audio.onerror = (error) => {
                    console.error("Audio playback error:", error);
                    audioRef.current = null;
                    setIsSpeaking(false);
                    isVoiceBusyRef.current = false;
                };

                audio.play().catch(error => {
                    console.error("Error playing audio:", error);
                    audioRef.current = null;
                    setIsSpeaking(false);
                    isVoiceBusyRef.current = false;
                });
            } else {
                console.error("TTS failed:", ttsResult.error);
                setIsSpeaking(false);
                isVoiceBusyRef.current = false;
            }
        } catch (error) {
            console.error("Error converting response to speech:", error);
            setIsSpeaking(false);
        }
    };

    const closeVoiceInterface = () => {
        // Mark as closed first so any in-flight async callbacks don't reopen
        isVoiceActiveRef.current = false;
        isVoiceBusyRef.current = false;

        // Stop silence detection timer
        if (silenceTimerRef.current) {
            clearInterval(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }

        // Stop any playing TTS audio
        if (audioRef.current) {
            audioRef.current.onended = null; // prevent restartListening from firing
            audioRef.current.onerror = null;
            audioRef.current.pause();
            audioRef.current = null;
        }

        // Stop audio capture
        if (socketRef.current) {
            socketRef.current.emit("stop_audio");
        }

        if (processorRef.current) {
            processorRef.current.port.postMessage({ terminate: true });
            processorRef.current.disconnect();
            processorRef.current = null;
        }

        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }

        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
        }

        // Reset transcription refs
        transcriptionTextRef.current = "";
        confirmedTranscriptRef.current = "";

        // Reset all states
        setIsVoiceActive(false);
        setIsListening(false);
        setIsProcessing(false);
        setIsSpeaking(false);
        setVoiceTranscription("");
    };

    const handleVoiceButtonClick = () => {
        if (isVoiceActive) {
            closeVoiceInterface();
        } else {
            startVoiceInteraction();
        }
    };

    const renderFormattedAudit = (text) => {
        if (!text) return <p className="text-gray-500 italic">No audit information available</p>;

        // Split the text by sections that start with "###"
        const sections = text.split(/(?=### )/);

        return (
            <div className="text-gray-70">
                {sections.map((section, sectionIndex) => {
                    // Skip empty sections
                    if (!section.trim()) return null;

                    // Extract the section header and content
                    const lines = section.split('\n');
                    const headerMatch = lines[0].match(/^### (.+)$/);

                    if (!headerMatch) return (
                        <div key={`section-${sectionIndex}`} className="mb-4">
                            <p>{section}</p>
                        </div>
                    );

                    const sectionTitle = headerMatch[1];
                    const sectionContent = lines.slice(1).join('\n').trim();

                    // Process the content based on the section type
                    return (
                        <div key={`section-${sectionIndex}`} className="mb-6">
                            <h3 className="text-lg font-bold text-primary mb-2">
                                {sectionTitle}
                            </h3>

                            <div className="pl-2 border-l-2 border-primaryLight">
                                {/* Handle different section formats */}
                                {sectionTitle === "Guardrails" || sectionTitle === "Overview" ? (
                                    <p className="text-sm">{sectionContent}</p>
                                ) : (
                                    <div className="text-sm">
                                        {sectionContent.split('\n').map((line, lineIndex) => {
                                            // Skip empty lines
                                            if (!line.trim()) return null;

                                            // Format lines with bullet points
                                            if (line.trim().startsWith('-')) {
                                                const bulletContent = line.replace(/^-\s*/, '');
                                                return (
                                                    <div key={`line-${sectionIndex}-${lineIndex}`} className="flex mb-1">
                                                        <span className="mr-2">•</span>
                                                        <span
                                                            dangerouslySetInnerHTML={{
                                                                __html: bulletContent
                                                                    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            }

                                            // Format lines with "key: value" pattern
                                            const keyValueMatch = line.match(/^- \*\*([^:]+):\*\* (.+)$/);
                                            if (keyValueMatch) {
                                                return (
                                                    <div key={`line-${sectionIndex}-${lineIndex}`} className="mb-1">
                                                        <strong className="text-gray-800">{keyValueMatch[1]}:</strong> {keyValueMatch[2]}
                                                    </div>
                                                );
                                            }

                                            // Default formatting for other lines
                                            return (
                                                <p
                                                    key={`line-${sectionIndex}-${lineIndex}`}
                                                    className="mb-1"
                                                    dangerouslySetInnerHTML={{
                                                        __html: line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderQueryPopup = () => {
        if (queryPopupIndex === null || !messages[queryPopupIndex]) return null;

        const msg = messages[queryPopupIndex];
        const hasBlockReason = !!msg.block_reason;
        const isLoadingAudit = msg.isLoadingAudit;
        const auditSummary = msg.audit;

        // Set active tab based on available data without using useEffect
        if (hasBlockReason && activeTab !== "guardrail") {
            // Use setTimeout to avoid state updates during render
            setTimeout(() => setActiveTab("guardrail"), 0);
        } else if (!hasBlockReason && activeTab !== "audit") {
            setTimeout(() => setActiveTab("audit"), 0);
        }

        return (
            <div
                className="fixed z-[1001] backdrop-blur-sm bg-black/30 animate-fadeIn"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                }}
            >
                <div
                    className="bg-white w-[100%] max-h-[100%] overflow-hidden flex flex-col"
                    ref={popupRef}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        maxWidth: '80%',
                        maxHeight: '80%',
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(0,0,0,0.07)'
                    }}
                >
                    <div className="px-4 py-3.5 border-b border-gray-100 flex justify-between items-center">
                        <h2 className="text-base font-semibold text-[#374151]">
                            {hasBlockReason ? "Guardrail Details" : "Audit Information"}
                        </h2>
                        <button
                            onClick={() => toggleQueryPopup(null)}
                            className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-lg transition-all duration-300"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    <div className="overflow-y-auto p-4 flex-grow" style={{ backgroundColor: '#f8f9fc' }}>
                        {hasBlockReason ? (
                            <div className="bg-white p-4 rounded-2xl border border-red-100 shadow-[0_4px_6px_rgba(0,0,0,0.07)]">
                                <div className="bg-red-50 p-4 rounded-xl border border-red-200 mt-2">
                                    <p className="text-sm text-red-800 whitespace-pre-wrap" style={{ lineHeight: '1.65' }}>{msg.block_reason}</p>
                                </div>
                                <p className="text-xs text-gray-500 mt-4" style={{ lineHeight: '1.6' }}>
                                    This message was blocked to ensure compliance with our content policies and guidelines.
                                </p>
                            </div>
                        ) : (
                            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-[0_4px_6px_rgba(0,0,0,0.07)]">
                                {isLoadingAudit ? (
                                    <div className="flex flex-col items-center justify-center py-8 gap-3">
                                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
                                        <p className="text-sm text-gray-500">Loading audit information...</p>
                                    </div>
                                ) : auditSummary ? (
                                    <div className="text-[#374151]">
                                        {renderFormattedAudit(auditSummary)}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 italic text-center py-4">No audit information available</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
                        <button
                            onClick={() => toggleQueryPopup(null)}
                            className={`px-4 py-1.5 text-sm text-white rounded-xl transition-all duration-300 hover:opacity-90 hover:scale-[1.02] ${hasBlockReason ? 'bg-red-500' : 'bg-primary'}`}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };


    const fetchAuditSummary = async (messageIndex) => {
        if (!messages[messageIndex]) return;

        // Get the run ID from the message
        const messageRunId = messages[messageIndex].run_id;

        // Update the message to show loading state
        setMessages(prevMessages => {
            const updatedMessages = [...prevMessages];
            updatedMessages[messageIndex] = {
                ...updatedMessages[messageIndex],
                isLoadingAudit: true
            };
            return updatedMessages;
        });

        if (!messageRunId) {
            // Update message with error
            setMessages(prevMessages => {
                const updatedMessages = [...prevMessages];
                updatedMessages[messageIndex] = {
                    ...updatedMessages[messageIndex],
                    audit: "No run ID available for this message.",
                    isLoadingAudit: false
                };
                return updatedMessages;
            });
            return;
        }

        try {
            const auditData = await wealthChatbotService.getSummarizedAudit(messageRunId);

            // Update the message with the audit data
            setMessages(prevMessages => {
                const updatedMessages = [...prevMessages];
                updatedMessages[messageIndex] = {
                    ...updatedMessages[messageIndex],
                    audit: auditData.response || 'No audit information available.',
                    block_reason: auditData.block_reason || updatedMessages[messageIndex].block_reason,
                    isLoadingAudit: false
                };
                return updatedMessages;
            });

            // If this message has a block reason, set the active tab to guardrail
            if (auditData.block_reason) {
                setActiveTab("guardrail");
            } else {
                setActiveTab("audit");
            }
        } catch (error) {
            console.error("Error fetching audit information:", error);

            // Update message with error
            setMessages(prevMessages => {
                const updatedMessages = [...prevMessages];
                updatedMessages[messageIndex] = {
                    ...updatedMessages[messageIndex],
                    audit: "Failed to load audit information. Please try again.",
                    isLoadingAudit: false
                };
                return updatedMessages;
            });
        }
    };

    const styleText = (text) => {
        let formattedText = text;

        if (text.includes('|') && text.includes('\n')) {
            const lines = text.split('\n');
            let inTable = false;
            let tableHTML = '<div class="w-full overflow-x-auto my-2.5"><table class="w-full border-collapse text-sm">';
            let processedLines = [];
            let headerCells = [];
            let tableRowIndex = 0;

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();

                if (line.includes('|')) {
                    if (!inTable) {
                        inTable = true;
                        tableRowIndex = 0;
                        tableHTML = '<div class="w-full overflow-x-auto my-2.5"><table class="w-full border-collapse text-sm">';
                    }

                    const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell);

                    if (line.includes('---')) {
                        continue;
                    }

                    tableHTML += '<tr>';
                    if (tableRowIndex === 0) {
                        // Header row - store column names
                        headerCells = cells;
                        cells.forEach(cell => {
                            tableHTML += `<th style="padding: 0.5rem; text-align: left; border: 1px solid #e5e7eb; background-color: #f9fafb; font-weight: 600;">${cell}</th>`;
                        });
                    } else {
                        // Data row - apply alignment based on column
                        cells.forEach((cell, colIndex) => {
                            const headerName = headerCells[colIndex] || '';
                            const isNumericColumn = ['Quantity', 'Purchase Price', 'Current Price', 'Value'].includes(headerName);
                            const alignment = isNumericColumn ? 'right' : 'left';
                            tableHTML += `<td style="padding: 0.5rem; text-align: ${alignment}; border: 1px solid #e5e7eb;">${cell}</td>`;
                        });
                    }
                    tableHTML += '</tr>';
                    tableRowIndex++;
                } else {
                    if (inTable) {
                        tableHTML += '</table></div>';
                        processedLines.push(tableHTML);
                        inTable = false;
                        headerCells = [];
                        tableRowIndex = 0;
                    }
                    processedLines.push(line);
                }
            }

            if (inTable) {
                tableHTML += '</table></div>';
                processedLines.push(tableHTML);
            }

            formattedText = processedLines.join('\n');
        }

        return formattedText
            .replace(/#### (.*?)(\n|$)/g, "<h4 class='text-base font-semibold my-2'>$1</h4>")
            .replace(/### (.*?)(\n|$)/g, "<h3 class='text-lg font-semibold my-2'>$1</h3>")
            .replace(/## (.*?)(\n|$)/g, "<h2 class='text-xl font-semibold my-2'>$1</h2>")
            .replace(/# (.*?)(\n|$)/g, "<h1 class='text-2xl font-bold my-2'>$1</h1>")
            .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
            .replace(/\*(.*?)\*/g, "<i>$1</i>")
            .replace(/~~(.*?)~~/g, "<s>$1</s>")
            .replace(/\n/g, "<br>");
    };

    const toggleQueryPopup = (index) => {
        // If we're opening the popup (index is not null and different from current)
        if (index !== null && queryPopupIndex !== index) {
            fetchAuditSummary(index);
        }

        setQueryPopupIndex(prevIndex => (prevIndex === index ? null : index));
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
            setIsExpanded(false);
        }, 300);
    };

    const handleExpand = () => {
        setIsExpanded(prevState => !prevState);
    };

    const getPageName = () => {
        const path = location.pathname.substring(1);
        if (!path) return 'WealthManagement';
        return path.split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('')
            || 'WealthManagement';
    };

    const handleCreateWidget = async () => {
        if (!userId || !cust_id || !use_case_id) {
            console.error("Missing required parameters for widget creation");
            throw new Error("Missing required parameters");
        }

        setIsCreatingWidget(true);

        try {
            const result = await wealthChatbotService.createWidget(
                userId,
                cust_id,
                use_case_id,
                tab_id
            );


            // Add a message to the chat
            setMessages(prevMessages => [
                ...prevMessages,
                {
                    text: "Widget created successfully! The dashboard will update shortly.",
                    sender: "bot"
                }
            ]);

            // Call the callback if provided
            if (typeof onWidgetCreated === 'function') {
                onWidgetCreated(result);
            }

            return result;
        } catch (error) {
            console.error("Error creating widget:", error);

            // Add an error message to the chat
            setMessages(prevMessages => [
                ...prevMessages,
                {
                    text: `Failed to create widget: ${error.message}`,
                    sender: "bot"
                }
            ]);

            throw error;
        } finally {
            setIsCreatingWidget(false);
        }
    };

    const handleSendMessage = async (queryOverride = null, templateId = null) => {
        const messageText = queryOverride ?? input;
        if (messageText.trim() === "" && !files.length) return;

        const userMessage = { text: messageText, sender: "user" };
        setMessages(prevMessages => [...prevMessages, userMessage]);
        setQueryPopupIndex(null);
        setInput("");
        setIsBotTyping(true);

        try {
            // Add file messages if any
            if (files.length > 0) {
                const fileMessages = files.map(file => ({
                    type: "file",
                    fileName: file.name,
                    fileType: file.type,
                    fileURL: URL.createObjectURL(file),
                    sender: "user",
                }));
                setMessages(prevMessages => [...prevMessages, ...fileMessages]);
            }

            const response = useNovaV2
                ? await wealthChatbotService.sendMessageV2(messageText, cust_id, userId, use_case_id, llmConfig, templateId)
                : await wealthChatbotService.sendMessage(messageText, files, cust_id, userId, getPageName(), use_case_id, pendingApprovalRunIdRef.current);

            setMessages(prevMessages => [...prevMessages, {
                text: "",
                sender: "bot",
                isStreaming: true
            }]);

            let accumulatedText = "";
            let sqlQuery = null;
            const shownStatuses = new Set();
            try {
                const { createParser } = await import('eventsource-parser');

                const parser = createParser({
                    onEvent: (event) => {
                        const type = event.event;
                        if (type === "filler" || type === "interrupt" || type === "final") {
                            try {
                                const parsedData = JSON.parse(event.data);

                                if (parsedData.ERROR && typeof parsedData.ERROR === 'string') {
                                    throw new Error(parsedData.ERROR);
                                }

                                if (type === "filler") {
                                    // Each filler event is a discrete status step — show as its own bubble
                                    const statusText = (parsedData.data?.response ?? (typeof parsedData.data === 'string' ? parsedData.data : '')).trim();
                                    if (statusText && !shownStatuses.has(statusText)) {
                                        shownStatuses.add(statusText);
                                        setMessages(prev => {
                                            const msgs = [...prev];
                                            // Insert before the streaming placeholder (last message)
                                            msgs.splice(msgs.length - 1, 0, { text: statusText, sender: "bot", isStatus: true, isLoading: true });
                                            return msgs;
                                        });
                                    }
                                    return;
                                }

                                // interrupt or final: build the answer
                                if (parsedData.data?.query) {
                                    sqlQuery = parsedData.data.query;
                                }

                                if (parsedData.data?.response) {
                                    accumulatedText += parsedData.data.response;
                                } else if (parsedData.data) {
                                    accumulatedText += parsedData.data;
                                }

                                if (parsedData.data?.run_id) {
                                    runIdRef.current = parsedData.data.run_id;
                                }

                                // Track whether we're awaiting a reply to an interrupt, and which
                                // run it belongs to, so the next message can echo it back.
                                if (type === "interrupt") {
                                    pendingApprovalRunIdRef.current = parsedData.data?.run_id || null;
                                } else if (type === "final") {
                                    pendingApprovalRunIdRef.current = null;
                                }

                                setMessages(prevMessages => {
                                    const updatedMessages = [...prevMessages];
                                    const lastIndex = updatedMessages.length - 1;
                                    updatedMessages[lastIndex] = {
                                        text: styleText(accumulatedText),
                                        sender: "bot",
                                        isStreaming: type !== "final" && type !== "interrupt",
                                        ...(sqlQuery && { query: sqlQuery }),
                                        ...(parsedData.data?.block_reason && { block_reason: parsedData.data.block_reason }),
                                        run_id: parsedData.data?.run_id || runIdRef.current,
                                        audit: null,
                                        isLoadingAudit: false,
                                    };
                                    return updatedMessages;
                                });

                                if (type === "final" || type === "interrupt") {
                                    setIsBotTyping(false);
                                    setMessages(prev => prev.map(m => m.isStatus ? { ...m, isLoading: false } : m));

                                    if (parsedData.data?.graph_html) {
                                        fetch(buildApiUrl('NOVA', `${parsedData.data.graph_html}`), { method: "GET" })
                                            .then(graphResponse => {
                                                if (!graphResponse.ok) throw new Error("Failed to fetch graph");
                                                return graphResponse.text();
                                            })
                                            .then(graphHtml => {
                                                setMessages(prevMessages => [
                                                    ...prevMessages,
                                                    { type: "iframe-html", html: graphHtml, sender: "bot" }
                                                ]);
                                            })
                                            .catch(graphError => {
                                                console.error("Error fetching graph:", graphError);
                                                setMessages(prevMessages => [
                                                    ...prevMessages,
                                                    { type: "iframe", url: wealthChatbotService.buildApiUrl('NOVA', parsedData.data.graph_html), sender: "bot" }
                                                ]);
                                            });
                                    }
                                }
                            } catch (error) {
                                console.error("Error processing event data:", error);
                            }
                        }
                    }
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();

                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    parser.feed(chunk);
                }
            } catch (streamError) {
                console.error("Stream processing error:", streamError);
                throw streamError;
            }
        } catch (error) {
            console.error("Error in handleSendMessage:", error);
            pendingApprovalRunIdRef.current = null;
            setMessages(prevMessages => [
                ...prevMessages,
                {
                    text: error.message || "Error connecting to server.",
                    sender: "bot"
                }
            ]);
        } finally {
            setIsBotTyping(false);
            setFiles([]); // Clear files after sending
            if (fileInputRef.current) {
                fileInputRef.current.value = ""; // Reset file input
            }
        }
    };

    const handleFileUpload = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        if (selectedFiles.length === 0) return;

        setFiles(prev => [...prev, ...selectedFiles]);
        setIsUploading(true);
        setUploadSuccess(null);

        try {
            // Upload files using the userId from props or session storage
            await wealthChatbotService.uploadFiles(selectedFiles, userId);
            setUploadSuccess(true);

            // Add success message to chat
            const fileMessages = selectedFiles.map(file => ({
                type: "file",
                fileName: file.name,
                fileType: file.type,
                fileURL: URL.createObjectURL(file),
                sender: "user",
            }));

            setMessages(prevMessages => [
                ...prevMessages,
                ...fileMessages,
                {
                    text: `${selectedFiles.length > 1 ? 'Files have' : 'File has'} been successfully uploaded. You can now ask questions about ${selectedFiles.length > 1 ? 'their' : 'its'} content.`,
                    sender: "bot",
                },
            ]);

        } catch (error) {
            console.error("Error uploading files:", error);
            setUploadSuccess(false);

            // Add error message to chat
            const fileMessages = selectedFiles.map(file => ({
                type: "file",
                fileName: file.name,
                fileType: file.type,
                fileURL: URL.createObjectURL(file),
                sender: "user",
            }));

            setMessages(prevMessages => [
                ...prevMessages,
                ...fileMessages,
                {
                    text: `Sorry, there was an error uploading your ${selectedFiles.length > 1 ? 'files' : 'file'}: ${error.message}. Please try again.`,
                    sender: "bot",
                },
            ]);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleContinueInNova = () => {
        navigate("/nova", { state: { chatMessages: messages, cust_id } });
    };

    const handleRateMessage = (messageIndex, isGood) => {
        if (messages[messageIndex].sender !== 'bot' || ratedMessages[messageIndex]) {
            return;
        }

        console.log(isGood ? "Good answer" : "Bad answer");

        setRatedMessages(prev => ({
            ...prev,
            [messageIndex]: isGood ? 'good' : 'bad'
        }));
    };

    return (
        <div className="fixed bottom-5 right-5 z-[1000] font-sans">
            {!isOpen && (
                <button
                    className="w-[52px] h-[52px] rounded-full text-white border-none flex items-center justify-center cursor-pointer shadow-[0_4px_14px_rgba(0,0,0,0.18)] transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-110 hover:shadow-[0_6px_20px_rgba(0,0,0,0.22)]"
                    style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' }}
                    onClick={() => setIsOpen(true)}
                >
                    <MessageCircle size={24} />
                </button>
            )}

            {isOpen && (
                <div
                    className="fixed bottom-[30px] right-5 bg-white flex flex-col overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] animate-fadeIn"
                    style={{
                        borderRadius: '20px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(0,0,0,0.07)',
                        width: isExpanded ? '85vw' : 'clamp(340px, 40vw, 480px)',
                        height: isExpanded ? '80vh' : '60vh',
                        backgroundColor: 'white',
                        position: 'fixed',
                    }}
                >
                    <ChatHeader
                        isExpanded={isExpanded}
                        handleExpand={handleExpand}
                        handleClose={handleClose}
                        handleContinueInNova={handleContinueInNova}
                        handleCreateWidget={handleCreateWidget}
                        isCreatingWidget={isCreatingWidget}
                        useNovaV2={useNovaV2}
                        onToggleV2={() => setUseNovaV2(v => !v)}
                    />

                    <div
                        className="flex-1 overflow-y-auto flex flex-col"
                        style={{ backgroundColor: '#F9FAFB', padding: '16px', gap: '0' }}
                        role="log"
                        aria-live="polite"
                        aria-label="Chat messages"
                    >
                        {messages.map((msg, index) => (
                            <ChatMessage
                                key={index}
                                msg={msg}
                                index={index}
                                userIsAdmin={userIsAdmin}
                                queryPopupIndex={queryPopupIndex}
                                toggleQueryPopup={toggleQueryPopup}
                                popupRef={popupRef}
                                ratedMessages={ratedMessages}
                                handleRateMessage={handleRateMessage}
                                isExpanded={isExpanded}
                                hasBlockReason={!!msg.block_reason}
                                isBotTyping={isBotTyping}
                            />
                        ))}

                        {isBotTyping && <TypingIndicator />}

                        {isUploading && (
                            <div
                                className="self-start flex items-center gap-2.5 animate-slideInLeft"
                                style={{
                                    padding: '12px 16px', borderRadius: '20px', borderBottomLeftRadius: '4px',
                                    backgroundColor: '#F3F4F6', color: '#1F2937',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                    fontSize: '14px', lineHeight: '1.5',
                                    margin: '8px 0',
                                }}
                            >
                                <div className="flex items-center gap-1.5">
                                    {[0, 0.2, 0.4].map((delay, i) => (
                                        <span key={i} className="animate-typingDots" style={{
                                            display: 'inline-block', width: '7px', height: '7px',
                                            borderRadius: '50%', backgroundColor: 'var(--color-primary)',
                                            opacity: 0.4, animationDelay: `${delay}s`,
                                        }} />
                                    ))}
                                </div>
                                <span>Uploading your file...</span>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {isVoiceActive && (
                        <div className="absolute inset-0 z-[1002] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                            <VoiceSphere
                                isActive={isVoiceActive}
                                isListening={isListening}
                                isProcessing={isProcessing}
                                isSpeaking={isSpeaking}
                                transcription={voiceTranscription}
                                onClose={closeVoiceInterface}
                            />
                        </div>
                    )}

                    {useNovaV2 && suggestions.length > 0 && (
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                padding: '10px 16px 4px',
                                borderTop: '1px solid #E5E7EB',
                                background: 'white',
                            }}
                        >
                            {suggestions.map(s => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => {
                                        setSuggestions(prev => prev.filter(x => x.id !== s.id));
                                        handleSendMessage(s.query, s.id);
                                    }}
                                    disabled={isBotTyping}
                                    title={s.description}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '16px',
                                        border: '1px solid var(--color-secondary, #06B6D4)',
                                        background: 'white',
                                        color: 'var(--color-primary, #0EA5E9)',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        cursor: isBotTyping ? 'not-allowed' : 'pointer',
                                        opacity: isBotTyping ? 0.5 : 1,
                                        transition: 'all 0.15s ease',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                    onMouseEnter={e => {
                                        if (!isBotTyping) {
                                            e.currentTarget.style.background = 'var(--color-secondary, #06B6D4)';
                                            e.currentTarget.style.color = 'white';
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.background = 'white';
                                        e.currentTarget.style.color = 'var(--color-primary, #0EA5E9)';
                                    }}
                                >
                                    {s.title}
                                </button>
                            ))}
                        </div>
                    )}

                    <ChatInput
                        input={input}
                        setInput={setInput}
                        handleSendMessage={handleSendMessage}
                        fileInputRef={fileInputRef}
                        handleFileUpload={handleFileUpload}
                        isBotTyping={isBotTyping}
                        onVoiceButtonClick={handleVoiceButtonClick}
                    />

                    {queryPopupIndex !== null && renderQueryPopup()}
                </div>
            )}
        </div>
    );
};

export default WealthChatBot;